/**
 * PathNav Sitemap Engine v1.1.0
 * Matches reference mindmap from tweeporter.ca header navigation
 * 
 * Key specs (from PATHNAV_MINDMAP_SPEC.md):
 * - 3-tier sizing: Focused (180×50), Children (150×42), Others (110×32)
 * - Opacity: Focused+Children=100%, Others=55%
 * - Compact spacing: siblings 8px, children 10px, grandchildren 6px
 * - Column layout: Parent | Focused+Siblings | Children | Grandchildren
 */

// Default node format
const DEFAULT_FORMAT = {
  start: { background: '#10b981', text: '#ffffff', border: '#10b981' },
  standard: { background: '#667eea', text: '#ffffff', border: '#5a67d8' },
  light: { background: '#a5b4fc', text: '#1e293b', border: '#818cf8' },
  unbounded: { background: 'transparent', text: '#667eea', border: '#667eea' },
  lines: { color: '#94a3b8', activeColor: '#667eea' }
};

// Helper to extract preview text
const getDescriptionPreview = (text, limit = 80) => {
  if (!text) return '';
  const cleaned = text
    .replace(/\[\[([^|\]]+)\|[^\]]+\]\]/g, '$1')
    .replace(/\[\[([^\]]+)\]\]/g, '$1')
    .trim();
  const lines = cleaned.split('\n').filter(line => line.trim());
  const firstLine = lines[0] || '';
  return firstLine.length > limit ? firstLine.substring(0, limit) + '...' : firstLine;
};

export const generatePathNavSitemapHtml = (graph, settings = {}) => {
  const nodes = graph.nodes || [];
  const edges = graph.edges || [];
  
  const {
    nodeFormat = DEFAULT_FORMAT,
    showDescription = false,
    descriptionCharLimit = 80,
    focusBorderColor = '#f97316',
    startMinimized = true,
    storageKey = 'pathnav_minimized'
  } = settings;
  
  // Merge formats
  const format = {
    start: { ...DEFAULT_FORMAT.start, ...nodeFormat?.start },
    standard: { ...DEFAULT_FORMAT.standard, ...nodeFormat?.standard },
    light: { ...DEFAULT_FORMAT.light, ...nodeFormat?.light },
    unbounded: { ...DEFAULT_FORMAT.unbounded, ...nodeFormat?.unbounded },
    lines: { ...DEFAULT_FORMAT.lines, ...nodeFormat?.lines }
  };
  
  // 3-Tier Node Sizing (per spec)
  const TIER1_W = 180, TIER1_H = 50;  // Focused
  const TIER2_W = 150, TIER2_H = 42;  // Children
  const TIER3_W = 110, TIER3_H = 32;  // Parent, siblings, grandchildren
  
  // Compact spacing (per spec)
  const GAP_X = 40;
  const GAP_SIBLINGS = 8;
  const GAP_CHILDREN = 10;
  const GAP_GRANDCHILDREN = 6;
  
  // Build tree structure (same as WordPress export)
  const allEdges = [];
  edges.forEach(edge => {
    const fromId = edge.from || edge.source;
    const toId = edge.to || edge.target;
    if (fromId && toId && fromId !== toId) {
      allEdges.push({ from: fromId, to: toId });
    }
  });
  
  const tempHasParent = new Set();
  allEdges.forEach(e => tempHasParent.add(e.to));
  
  const rootNode = nodes.find(n => (n.name === 'Home' || n.name === 'Start') && !tempHasParent.has(n.id)) 
    || nodes.find(n => !tempHasParent.has(n.id)) 
    || nodes[0];
  
  if (!rootNode) return `<div>No nodes to display</div>`;
  
  // Build tree from root
  const childrenMap = new Map();
  const parentMap = new Map();
  const visited = new Set();
  
  const buildTree = (nodeId) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    const forwardEdges = allEdges.filter(e => e.from === nodeId && !visited.has(e.to));
    if (forwardEdges.length > 0) {
      childrenMap.set(nodeId, forwardEdges.map(e => e.to));
      forwardEdges.forEach(e => {
        parentMap.set(e.to, nodeId);
        buildTree(e.to);
      });
    }
  };
  buildTree(rootNode.id);
  
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const treeNodeIds = Array.from(visited);
  
  const getNodeColors = (node, isRoot) => {
    if (isRoot) return format.start;
    const style = node.nodeStyle || 'standard';
    return format[style] || format.standard;
  };
  
  // Build node data for JS - include index for stable ordering
  const nodeDataList = treeNodeIds.map((nodeId, index) => {
    const node = nodeMap.get(nodeId);
    const isRoot = nodeId === rootNode.id;
    const colors = getNodeColors(node, isRoot);
    const children = childrenMap.get(nodeId) || [];
    const parent = parentMap.get(nodeId) || null;
    
    return {
      id: nodeId,
      name: node.displayName || node.name,
      url: node.url || null,
      desc: showDescription ? getDescriptionPreview(node.body, descriptionCharLimit) : '',
      bg: colors.background,
      text: colors.text,
      border: colors.border,
      isRoot,
      children,
      parent,
      order: index  // Preserve original tree traversal order
    };
  });
  
  // Serialize for JS
  const nodesJson = JSON.stringify(nodeDataList);
  const childrenMapJson = JSON.stringify(Array.from(childrenMap.entries()));
  const title = graph.storyTitle || 'Site Navigation';
  const version = '1.1.0';
  
  return `<!-- PathNav Sitemap v${version} | Generated: ${new Date().toISOString().split('T')[0]} -->
<div id="pathnav-sitemap" class="pathnav-container" data-version="${version}">
<style>
.pathnav-container{font-family:system-ui,-apple-system,sans-serif;position:relative;max-width:100%;overflow:hidden;}
.pathnav-header{display:flex;align-items:center;justify-content:space-between;padding:10px 16px;background:#f1f5f9;border-radius:12px 12px 0 0;border:1px solid #e2e8f0;border-bottom:none;}
.pathnav-title{font-size:15px;font-weight:600;color:#334155;}
.pathnav-toggle{background:#fff;border:1px solid #cbd5e1;border-radius:6px;padding:6px 12px;cursor:pointer;font-size:13px;font-weight:500;color:#475569;transition:all .15s;}
.pathnav-toggle:hover{background:#f8fafc;border-color:#94a3b8;}
.pathnav-canvas{position:relative;background:#f8fafc;border:1px solid #e2e8f0;border-radius:0 0 12px 12px;overflow:auto;transition:height 0.3s ease;min-height:80px;}
.pathnav-canvas.minimized{overflow:hidden;}
.pathnav-inner{position:relative;min-width:100%;min-height:100%;}
.pathnav-svg{position:absolute;top:0;left:0;pointer-events:none;}
.pathnav-path{fill:none;stroke:${focusBorderColor};stroke-width:2.5;}
.pathnav-node{position:absolute;border-radius:10px;font-weight:500;cursor:pointer;display:flex;align-items:center;box-shadow:0 2px 6px rgba(0,0,0,0.1);box-sizing:border-box;overflow:hidden;transition:all 0.15s ease;}
.pathnav-node:hover{transform:translateY(-2px);box-shadow:0 4px 12px rgba(0,0,0,0.15);opacity:1 !important;}
.pathnav-node.tier1{font-size:14px;border:3px solid ${focusBorderColor};box-shadow:0 4px 16px rgba(249,115,22,0.3);}
.pathnav-node.tier2{font-size:12px;}
.pathnav-node.tier3{font-size:11px;opacity:0.55;}
.pathnav-node-inner{display:flex;flex-direction:column;justify-content:center;padding:8px 12px;width:100%;height:100%;box-sizing:border-box;}
.pathnav-node-header{display:flex;align-items:center;justify-content:space-between;width:100%;}
.pathnav-node-name{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.pathnav-link{display:flex;align-items:center;justify-content:center;width:20px;height:20px;background:rgba(255,255,255,0.2);border-radius:50%;margin-left:6px;text-decoration:none;color:inherit;flex-shrink:0;}
.pathnav-link:hover{background:rgba(255,255,255,0.35);}
.pathnav-link svg{width:11px;height:11px;}
</style>
<div class="pathnav-header">
<span class="pathnav-title">${title}</span>
<button class="pathnav-toggle" id="pathnav-toggle-btn">${startMinimized ? 'Expand' : 'Collapse'}</button>
</div>
<div class="pathnav-canvas${startMinimized ? ' minimized' : ''}" id="pathnav-canvas">
<div class="pathnav-inner" id="pathnav-inner">
<svg class="pathnav-svg" id="pathnav-svg"></svg>
<div id="pathnav-nodes"></div>
</div>
</div>
<script>
(function(){
'use strict';
var NODES=${nodesJson};
var CHILDREN_MAP=new Map(${childrenMapJson});
var ROOT_ID='${rootNode.id}';
var STORAGE_KEY='${storageKey}';
var FOCUS_COLOR='${focusBorderColor}';

// 3-Tier Sizing (per spec)
var T1_W=${TIER1_W},T1_H=${TIER1_H};  // Focused
var T2_W=${TIER2_W},T2_H=${TIER2_H};  // Children
var T3_W=${TIER3_W},T3_H=${TIER3_H};  // Others
var GAP_X=${GAP_X};
var GAP_SIB=${GAP_SIBLINGS};
var GAP_CHILD=${GAP_CHILDREN};
var GAP_GC=${GAP_GRANDCHILDREN};

var state={
focusedId:ROOT_ID,
isMinimized:localStorage.getItem(STORAGE_KEY)==='true'||(localStorage.getItem(STORAGE_KEY)===null&&${startMinimized})
};

var canvas=document.getElementById('pathnav-canvas');
var inner=document.getElementById('pathnav-inner');
var svg=document.getElementById('pathnav-svg');
var nodesEl=document.getElementById('pathnav-nodes');
var toggleBtn=document.getElementById('pathnav-toggle-btn');

function getNode(id){return NODES.find(function(n){return n.id===id;});}

function getVisibleNodes(){
var focused=getNode(state.focusedId);
if(!focused)return[];
var visible=[focused];
var children=CHILDREN_MAP.get(state.focusedId)||[];
children.forEach(function(cid){
var c=getNode(cid);if(c)visible.push(c);
var gc=CHILDREN_MAP.get(cid)||[];
gc.forEach(function(gcid){var g=getNode(gcid);if(g)visible.push(g);});
});
if(focused.parent){
var p=getNode(focused.parent);
if(p){
visible.push(p);
var siblings=CHILDREN_MAP.get(focused.parent)||[];
siblings.forEach(function(sid){if(sid!==state.focusedId){var s=getNode(sid);if(s)visible.push(s);}});
}
}
return visible;
}

// Returns tier: 1=focused, 2=children, 3=others
function getNodeTier(node){
if(node.id===state.focusedId)return 1;
var children=CHILDREN_MAP.get(state.focusedId)||[];
if(children.indexOf(node.id)!==-1)return 2;
return 3;
}

function getNodeType(node){
if(node.id===state.focusedId)return 'focused';
var children=CHILDREN_MAP.get(state.focusedId)||[];
if(children.indexOf(node.id)!==-1)return 'child';
var focused=getNode(state.focusedId);
if(focused&&focused.parent===node.id)return 'parent';
if(focused&&focused.parent){
var siblings=CHILDREN_MAP.get(focused.parent)||[];
if(siblings.indexOf(node.id)!==-1)return 'sibling';
}
return 'grandchild';
}

function render(){
if(state.isMinimized){
renderMinimized();
return;
}
var visible=getVisibleNodes();
var visibleIds=visible.map(function(n){return n.id;});

var positions=new Map();
var focused=getNode(state.focusedId);
if(!focused)return;

// Get structural relationships
var focusedChildren=CHILDREN_MAP.get(state.focusedId)||[];
var focusedParentId=focused.parent;
var siblings=[];
if(focusedParentId){
var allSiblings=CHILDREN_MAP.get(focusedParentId)||[];
siblings=allSiblings.filter(function(sid){return sid!==state.focusedId;});
}

// Column layout: Parent | Focused+Siblings | Children | Grandchildren
var col0X=15;
var col1X=col0X+T3_W+GAP_X;
var col2X=col1X+T1_W+GAP_X;
var col3X=col2X+T2_W+GAP_X;

var baseY=15;

// Parent (Tier 3)
if(focusedParentId&&visibleIds.indexOf(focusedParentId)!==-1){
positions.set(focusedParentId,{x:col0X,y:baseY,w:T3_W,h:T3_H,tier:3,type:'parent'});
}

// Focused (Tier 1)
positions.set(state.focusedId,{x:col1X,y:baseY,w:T1_W,h:T1_H,tier:1,type:'focused'});

// Siblings below focused (Tier 3, compact spacing)
var sibY=baseY+T1_H+GAP_SIB;
siblings.forEach(function(sid){
if(visibleIds.indexOf(sid)!==-1){
positions.set(sid,{x:col1X,y:sibY,w:T3_W,h:T3_H,tier:3,type:'sibling'});
sibY+=T3_H+GAP_SIB;
}
});

// Children (Tier 2, medium size)
var childY=baseY;
focusedChildren.forEach(function(cid){
if(visibleIds.indexOf(cid)!==-1){
positions.set(cid,{x:col2X,y:childY,w:T2_W,h:T2_H,tier:2,type:'child'});
childY+=T2_H+GAP_CHILD;
}
});

// Grandchildren (Tier 3, very compact)
var gcY=baseY;
focusedChildren.forEach(function(cid){
var gc=CHILDREN_MAP.get(cid)||[];
gc.forEach(function(gcid){
if(visibleIds.indexOf(gcid)!==-1){
positions.set(gcid,{x:col3X,y:gcY,w:T3_W,h:T3_H,tier:3,type:'grandchild'});
gcY+=T3_H+GAP_GC;
}
});
});

// Calculate canvas size
var maxX=0,maxY=0;
positions.forEach(function(p){
if(p.x+p.w>maxX)maxX=p.x+p.w;
if(p.y+p.h>maxY)maxY=p.y+p.h;
});
var canvasW=maxX+30,canvasH=Math.max(maxY+30,150);
inner.style.width=canvasW+'px';
inner.style.height=canvasH+'px';
canvas.style.height=canvasH+'px';
svg.setAttribute('width',canvasW);
svg.setAttribute('height',canvasH);

// Draw paths
svg.innerHTML='';
positions.forEach(function(pos,nodeId){
var node=getNode(nodeId);
if(!node)return;
// Draw to children only
var nodeChildren=CHILDREN_MAP.get(nodeId)||[];
nodeChildren.forEach(function(cid){
if(positions.has(cid)){
var childPos=positions.get(cid);
var px=pos.x+pos.w,py=pos.y+pos.h/2;
var cx=childPos.x,cy=childPos.y+childPos.h/2;
var mx=px+(cx-px)*0.5;
var path=document.createElementNS('http://www.w3.org/2000/svg','path');
path.setAttribute('d','M'+px+' '+py+' C'+mx+' '+py+', '+mx+' '+cy+', '+cx+' '+cy);
path.setAttribute('class','pathnav-path');
svg.appendChild(path);
}
});
// Draw from parent to focused
if(nodeId===focusedParentId&&positions.has(state.focusedId)){
var focusPos=positions.get(state.focusedId);
var px=pos.x+pos.w,py=pos.y+pos.h/2;
var cx=focusPos.x,cy=focusPos.y+focusPos.h/2;
var mx=px+(cx-px)*0.5;
var path=document.createElementNS('http://www.w3.org/2000/svg','path');
path.setAttribute('d','M'+px+' '+py+' C'+mx+' '+py+', '+mx+' '+cy+', '+cx+' '+cy);
path.setAttribute('class','pathnav-path');
svg.appendChild(path);
}
});

// Render nodes with tier-based styling
nodesEl.innerHTML='';
var posArray=[];
positions.forEach(function(pos,nodeId){posArray.push({nodeId:nodeId,pos:pos});});
posArray.sort(function(a,b){
var aNode=getNode(a.nodeId);
var bNode=getNode(b.nodeId);
return(aNode?aNode.order:0)-(bNode?bNode.order:0);
});
posArray.forEach(function(item){
var nodeId=item.nodeId;
var pos=item.pos;
var node=getNode(nodeId);
if(!node)return;
var div=document.createElement('div');
div.className='pathnav-node tier'+pos.tier;
div.style.left=pos.x+'px';
div.style.top=pos.y+'px';
div.style.width=pos.w+'px';
div.style.height=pos.h+'px';
div.style.background=node.bg;
div.style.color=node.text;
div.setAttribute('data-id',node.id);
div.setAttribute('tabindex','0');

var innerDiv=document.createElement('div');
innerDiv.className='pathnav-node-inner';
var header=document.createElement('div');
header.className='pathnav-node-header';
var nameSpan=document.createElement('span');
nameSpan.className='pathnav-node-name';
nameSpan.textContent=node.name;
header.appendChild(nameSpan);
if(node.url){
var link=document.createElement('a');
link.className='pathnav-link';
link.href=node.url;
link.target='_blank';
link.innerHTML='<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>';
link.onclick=function(e){e.stopPropagation();};
header.appendChild(link);
}
innerDiv.appendChild(header);
div.appendChild(innerDiv);

div.onclick=function(e){
if(e.target.closest('.pathnav-link'))return;
state.focusedId=node.id;
render();
};
div.onkeydown=function(e){
if(e.key==='Enter'||e.key===' '){e.preventDefault();state.focusedId=node.id;render();}
};
nodesEl.appendChild(div);
});
}

function renderMinimized(){
var rootNode=getNode(ROOT_ID);
if(!rootNode)return;
var w=T1_W,h=T1_H;
inner.style.width=(w+30)+'px';
inner.style.height=(h+30)+'px';
canvas.style.height=(h+30)+'px';
svg.setAttribute('width',w+30);
svg.setAttribute('height',h+30);
svg.innerHTML='';
nodesEl.innerHTML='';

var div=document.createElement('div');
div.className='pathnav-node tier1';
div.style.left='15px';
div.style.top='15px';
div.style.width=w+'px';
div.style.height=h+'px';
div.style.background=rootNode.bg;
div.style.color=rootNode.text;
div.setAttribute('data-id',rootNode.id);

var innerDiv=document.createElement('div');
innerDiv.className='pathnav-node-inner';
var header=document.createElement('div');
header.className='pathnav-node-header';
var nameSpan=document.createElement('span');
nameSpan.className='pathnav-node-name';
nameSpan.textContent=rootNode.name;
header.appendChild(nameSpan);
if(rootNode.url){
var link=document.createElement('a');
link.className='pathnav-link';
link.href=rootNode.url;
link.target='_blank';
link.innerHTML='<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>';
link.onclick=function(e){e.stopPropagation();};
header.appendChild(link);
}
innerDiv.appendChild(header);
div.appendChild(innerDiv);

div.onclick=function(e){
if(e.target.closest('.pathnav-link'))return;
state.isMinimized=false;
state.focusedId=ROOT_ID;
localStorage.setItem(STORAGE_KEY,'false');
canvas.classList.remove('minimized');
toggleBtn.textContent='Collapse';
render();
};
nodesEl.appendChild(div);
}

function toggleMinimized(){
state.isMinimized=!state.isMinimized;
localStorage.setItem(STORAGE_KEY,String(state.isMinimized));
canvas.classList.toggle('minimized',state.isMinimized);
toggleBtn.textContent=state.isMinimized?'Expand':'Collapse';
if(state.isMinimized)state.focusedId=ROOT_ID;
render();
}

toggleBtn.onclick=toggleMinimized;
if(state.isMinimized){
canvas.classList.add('minimized');
toggleBtn.textContent='Expand';
}
render();
})();
</script>
</div>
<!-- End PathNav Sitemap -->`;
};

// Keep old function for backward compatibility but redirect to new one
export const generatePathNavHtml = generatePathNavSitemapHtml;

// Helper to convert graph data to PathNav config format
export const graphToPathNavConfig = (graph, settings = {}) => {
  // This is now just a passthrough - the main function handles everything
  return { graph, settings };
};
